/**
 * What each build can do, side by side, for the Features page.
 *
 * The page leads with Deadlock's two builds, Internal and External -- one
 * purchase gets both, so "which one" is the question a buyer actually has --
 * and lets you add the other games as columns.
 *
 * Every tick is a control in that build's own menu. A blank means "not in
 * this build's menu", not "never": nothing is claimed that the menus do not
 * show. "partial" is for a feature a build has only part of, so it is
 * neither advertised as the full thing nor hidden. Sources:
 *   Deadlock Internal  the captures in public/menu (menu-01..16), and the
 *                      menu's own labels in AndromedaClient/GUI.
 *   Deadlock External  the captures (ext-01..04) and, because four captures
 *                      do not show every control, the build's full list of
 *                      menu strings in Reference-DeadlockEXT/localization.rs.
 *                      It is how "Recoil Control System" was found: the
 *                      captures alone would have left External looking
 *                      thinner than it is. A string there is not proof on its
 *                      own, though: "Lua Executor" is in the file, but the
 *                      developer confirmed External ships without Lua.
 *   CS2, FiveM         their captures (src/assets/pictures/cs2 external,
 *                      src/assets/menu-teal/product).
 *   Roblox             in beta with no captures yet: its published feature
 *                      notes in data/games.tsx.
 *
 * Evidence, per row (I = Internal, E = External):
 *   Aimbot         I menu-01; E ext-01 "Aimbot"; CS2 Aimbot tab; FiveM Combat; Roblox notes
 *   Silent aim     I menu-02; E "Silent Aimbot"; FiveM Combat "Silent Aim"; Roblox notes
 *   Triggerbot     I menu-03 "Players / Bots"; CS2 Triggerbot tab; FiveM Combat "TriggerBot".
 *                  E has "Soul Triggerbot" only, which is the Soul aim row.
 *   Soul aim       I menu-01 "Target Souls", menu-03 "Souls"; E "Souls" aim, "Soul Triggerbot"
 *   Recoil control I menu-01 "Recoil Control"; E "Recoil Control System"; CS2 "Standalone RCS"; FiveM Local
 *   Humanizer      I "Humanizer"; E "Aim Humanizer"
 *   Headshot cap   I menu-01 "Headshot Cap"
 *   Player ESP     I menu-04/05; E ext-02; CS2 Players tab; FiveM Visuals "Enemy ESP"; Roblox notes
 *   Radar          I menu-08; E "Map Radar", "Default Radar"; CS2 Radar tab; FiveM Visuals "Radar"
 *   Chams          CS2 Chams tab
 *   World ESP      I menu-07 NPCs, souls, breakables; E ext-02 jungles, neutrals, breakables;
 *                  CS2 Items, Bomb, Projectiles; FiveM "Vehicle ESP"
 *   Spectator list I "Spectator List"; E "Spectators"; CS2 Spectators tab
 *   Hero scripts   I menu-09 Heroes; E ext-03 Scripts
 *   Lua scripting  I menu-15. Not E, per the developer (2026-09-19), despite
 *                  a leftover "Lua Executor" string in its localization.
 *   Teleport       FiveM Teleport
 *   Vehicle tools  FiveM Vehicle
 *   Player mods    FiveM Local: GodMode, NoClip, Super Jump, Never Wanted
 *   Saved configs  I menu-16; E ext-04 "Config"; CS2 Config tab; FiveM Settings "Config"
 *   Stream proof   I menu-16 "Stream Proof"; FiveM Settings "Stream Proof";
 *                  E partial -- per the developer (2026-09-19); its menu
 *                  strings have no stream-proof toggle of their own.
 */

/* The page's title and description, here so the prerender (seo/prerenderEntry)
   and the page itself (pages/Features) cannot drift apart. */
export const FEATURES_TITLE = "Cheat Features — Deadlock Internal vs External, CS2 & FiveM";
export const FEATURES_DESCRIPTION =
  "Deadlock Internal vs External compared feature by feature: aimbot, silent aim, triggerbot, ESP, radar, soul aim, Lua. CS2, FiveM and Roblox alongside.";

export type MatrixColumn = {
  id: "dl-int" | "dl-ext" | "cs2" | "fivem" | "roblox";
  /** The product it belongs to: price, sales switch and link. */
  game: string;
  label: string;
  /** How the build runs, for the column head. */
  note?: string;
  /** Deadlock's two are always shown; the rest are added on request. */
  optional?: boolean;
};

export const MATRIX_COLUMNS: MatrixColumn[] = [
  { id: "dl-int", game: "deadlock-external", label: "Internal", note: "Inside the game" },
  { id: "dl-ext", game: "deadlock-external", label: "External", note: "Separate overlay" },
  { id: "cs2", game: "cs2-external", label: "CS2", optional: true },
  { id: "fivem", game: "fivem-external", label: "FiveM", optional: true },
  { id: "roblox", game: "roblox-external", label: "Roblox", optional: true },
];

/** true: the build has it. "partial": it has some of it. Absent: not in its menu. */
export type Cell = true | "partial";
type Has = Partial<Record<MatrixColumn["id"], Cell>>;
export type MatrixRow = { label: string; hint: string; has: Has };

const I = "dl-int";
const E = "dl-ext";
const CS = "cs2";
const FM = "fivem";
const RB = "roblox";

export const FEATURE_MATRIX: { group: string; rows: MatrixRow[] }[] = [
  {
    group: "Aim",
    rows: [
      { label: "Aimbot", hint: "FOV, smoothing and bone selection", has: { [I]: true, [E]: true, [CS]: true, [FM]: true, [RB]: true } },
      { label: "Silent aim", hint: "Hits without moving your view", has: { [I]: true, [E]: true, [FM]: true, [RB]: true } },
      { label: "Triggerbot", hint: "Fires when your crosshair is on a player", has: { [I]: true, [CS]: true, [FM]: true } },
      { label: "Soul aim", hint: "Aims and fires at souls to secure them", has: { [I]: true, [E]: true } },
      { label: "Recoil control", hint: "Pulls spray back onto the target", has: { [I]: true, [E]: true, [CS]: true, [FM]: true } },
      { label: "Humanizer", hint: "Lets your aim drift like a person's", has: { [I]: true, [E]: true } },
      { label: "Headshot cap", hint: "Keeps your headshot rate believable", has: { [I]: true } },
    ],
  },
  {
    group: "Visuals",
    rows: [
      { label: "Player ESP", hint: "Boxes, health, names and distance", has: { [I]: true, [E]: true, [CS]: true, [FM]: true, [RB]: true } },
      { label: "Radar", hint: "Enemies on a map overlay", has: { [I]: true, [E]: true, [CS]: true, [FM]: true } },
      { label: "Chams", hint: "Players coloured through walls", has: { [CS]: true } },
      { label: "World ESP", hint: "NPCs, items, bombs or vehicles", has: { [I]: true, [E]: true, [CS]: true, [FM]: true } },
      { label: "Spectator list", hint: "Shows who is watching you", has: { [I]: true, [E]: true, [CS]: true } },
    ],
  },
  {
    group: "Extras",
    rows: [
      { label: "Hero scripts", hint: "Ability scripts, set up per hero", has: { [I]: true, [E]: true } },
      { label: "Lua scripting", hint: "Run your own scripts", has: { [I]: true } },
      { label: "Teleport", hint: "Landmarks, waypoints, saved spots", has: { [FM]: true } },
      { label: "Vehicle tools", hint: "Spawn, repair, lock and unlock", has: { [FM]: true } },
      { label: "Player mods", hint: "GodMode, noclip, super jump", has: { [FM]: true } },
      { label: "Saved configs", hint: "Save a setup and load it back", has: { [I]: true, [E]: true, [CS]: true, [FM]: true } },
      { label: "Stream proof", hint: "Hidden from captures and streams", has: { [I]: true, [E]: "partial", [FM]: true } },
    ],
  },
];

/** Where Deadlock's two builds agree and differ, for the summary line --
    derived, so it cannot contradict the table. "shared" means both in full. */
export function buildDifference() {
  const rows = FEATURE_MATRIX.flatMap((g) => g.rows).filter((r) => r.has[I] || r.has[E]);
  const names = (keep: (r: MatrixRow) => boolean) => rows.filter(keep).map((r) => r.label);
  return {
    shared: names((r) => r.has[I] === true && r.has[E] === true),
    internalOnly: names((r) => !!r.has[I] && !r.has[E]),
    externalOnly: names((r) => !!r.has[E] && !r.has[I]),
    /** Full on one build, partial on the other. */
    externalPartial: names((r) => r.has[I] === true && r.has[E] === "partial"),
    internalPartial: names((r) => r.has[E] === true && r.has[I] === "partial"),
  };
}
